import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lightning, Clock, MapPin, CurrencyDollar, X, ShoppingCart, Star, Fire, Lock, SpinnerGap
} from '@phosphor-icons/react';
import { useWsSubscribe } from '../../contexts/WebSocketContext';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const RESERVATION_TTL = 60;

/**
 * Real-time "Lead Preview" notification feed for contractors.
 * Subscribes to `new_lead` WebSocket events and pushes a live, dismissible card stack
 * at the top of the contractor dashboard. Each card has a "Buy Lead" CTA that takes
 * the contractor straight to the marketplace detail page for that lead.
 */
export const LiveLeadPreview = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]); // newest first
  // reservedLeads: { [lead_id]: { until: number(ms), busy: bool } }
  const [reservedLeads, setReservedLeads] = useState({});
  const [, setNowTick] = useState(0); // forces countdown rerenders
  const dismissTimers = useRef({});

  // Countdown ticker — rerender once per second while any reservation is active
  useEffect(() => {
    if (Object.keys(reservedLeads).length === 0) return undefined;
    const id = setInterval(() => setNowTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [reservedLeads]);

  // Release reservation on unmount so the next contractor isn't stuck waiting
  useEffect(() => () => {
    Object.entries(reservedLeads).forEach(([leadId, info]) => {
      if (!info?.until || info.until <= Date.now()) return;
      // Fire-and-forget release on unmount; ignore errors
      axios.post(`${API_BASE}/leads/marketplace/${leadId}/release`, {}, { headers: authHeader() }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot cleanup
  }, []);

  const dismiss = useCallback((leadId) => {
    setLeads(prev => prev.filter(l => l.lead_id !== leadId));
    if (dismissTimers.current[leadId]) {
      clearTimeout(dismissTimers.current[leadId]);
      delete dismissTimers.current[leadId];
    }
    // If user dismisses a card they reserved, release the hold so others can grab it
    setReservedLeads(prev => {
      const info = prev[leadId];
      if (info?.until && info.until > Date.now()) {
        axios.post(`${API_BASE}/leads/marketplace/${leadId}/release`, {}, { headers: authHeader() }).catch(() => {});
      }
      const next = { ...prev };
      delete next[leadId];
      return next;
    });
  }, []);

  const reserveLead = useCallback(async (leadId) => {
    setReservedLeads(prev => ({ ...prev, [leadId]: { ...(prev[leadId] || {}), busy: true } }));
    try {
      const res = await axios.post(`${API_BASE}/leads/marketplace/${leadId}/reserve`, {}, { headers: authHeader() });
      const until = new Date(res.data.reserved_until).getTime();
      setReservedLeads(prev => ({ ...prev, [leadId]: { until, busy: false } }));

      // Clear the auto-dismiss timer — reserving means contractor is engaged
      if (dismissTimers.current[leadId]) {
        clearTimeout(dismissTimers.current[leadId]);
        delete dismissTimers.current[leadId];
      }

      // Auto-clear local reservation state after TTL so the badge resets
      setTimeout(() => {
        setReservedLeads(prev => {
          const cur = prev[leadId];
          if (cur && cur.until <= Date.now() + 250) {
            const next = { ...prev };
            delete next[leadId];
            return next;
          }
          return prev;
        });
      }, RESERVATION_TTL * 1000 + 500);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not reserve this lead';
      setReservedLeads(prev => ({ ...prev, [leadId]: { ...(prev[leadId] || {}), busy: false, error: detail } }));
      // Clear the error message after 4s
      setTimeout(() => {
        setReservedLeads(prev => {
          const cur = prev[leadId];
          if (!cur) return prev;
          const next = { ...prev };
          if (cur.until && cur.until > Date.now()) {
            next[leadId] = { until: cur.until };
          } else {
            delete next[leadId];
          }
          return next;
        });
      }, 4000);
    }
  }, []);

  const handleMessage = useCallback((msg) => {
    if (!msg || !msg.type) return;

    // New lead push
    if (msg.type === 'new_lead' && msg.lead) {
      const lead = msg.lead;
      setLeads(prev => {
        if (prev.some(l => l.lead_id === lead.lead_id)) return prev;
        return [lead, ...prev].slice(0, 4);
      });
      dismissTimers.current[lead.lead_id] = setTimeout(() => dismiss(lead.lead_id), 45000);
      return;
    }

    // Another contractor placed a hold — reflect "held by another" state on our card
    if (msg.type === 'lead_reserved' && msg.lead_id) {
      const until = msg.reserved_until ? new Date(msg.reserved_until).getTime() : Date.now() + 60000;
      setReservedLeads(prev => ({
        ...prev,
        [msg.lead_id]: { until, busy: false, by_other: true }
      }));
      // Disable auto-dismiss so contractor sees the held card and can wait
      if (dismissTimers.current[msg.lead_id]) {
        clearTimeout(dismissTimers.current[msg.lead_id]);
        delete dismissTimers.current[msg.lead_id];
      }
      return;
    }

    // Another contractor released — clear the held-by-other indicator
    if (msg.type === 'lead_released' && msg.lead_id) {
      setReservedLeads(prev => {
        const cur = prev[msg.lead_id];
        if (!cur || !cur.by_other) return prev; // don't stomp on our own reservation
        const next = { ...prev };
        delete next[msg.lead_id];
        return next;
      });
    }
  }, [dismiss]);

  // Subscribe to WS for `new_lead` events (shared connection from WebSocketProvider)
  useWsSubscribe(handleMessage);

  // Cleanup all timers on unmount
  useEffect(() => () => {
    Object.values(dismissTimers.current).forEach(clearTimeout);
    dismissTimers.current = {};
  }, []);

  if (leads.length === 0) return null;

  const gradeColor = (g) => ({
    A: 'bg-emerald-500 text-white',
    B: 'bg-lime-500 text-black',
    C: 'bg-amber-500 text-black',
    D: 'bg-orange-500 text-white',
    F: 'bg-red-500 text-white',
  }[g] || 'bg-amber-500 text-black');

  return (
    <div className="fixed top-20 right-4 z-[60] w-[min(380px,calc(100vw-2rem))] space-y-3 pointer-events-none" data-testid="live-lead-preview">
      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#475569] bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm border-2 border-black px-3 py-1.5 rounded-full w-fit shadow-[2px_2px_0px_#000] pointer-events-auto">
        <Fire size={14} weight="fill" className="animate-pulse" /> Live Lead Feed
        <span className="text-neutral-400 normal-case font-medium">({leads.length} fresh)</span>
      </div>
      {leads.map((lead, idx) => {
        const resInfo = reservedLeads[lead.lead_id];
        const remainingMs = resInfo?.until ? resInfo.until - Date.now() : 0;
        const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
        const isReserved = remainingSec > 0;
        const isMine = isReserved && !resInfo?.by_other;
        const isHeldByOther = isReserved && resInfo?.by_other;
        const isReserving = !!resInfo?.busy;

        // Color tier
        const borderColor = isMine
          ? 'border-emerald-500 shadow-[4px_4px_0px_#10b981]'
          : isHeldByOther
            ? 'border-neutral-400 shadow-[4px_4px_0px_#737373]'
            : 'border-[#3B82F6] shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#3B82F6]';

        const iconBg = isMine ? 'bg-emerald-500' : isHeldByOther ? 'bg-neutral-500' : 'bg-[#3B82F6]';

        return (
        <div
          key={lead.lead_id}
          className={`relative bg-white dark:bg-neutral-800 border-2 rounded-xl p-4 animate-in slide-in-from-right-4 fade-in duration-500 pointer-events-auto ${borderColor} ${isHeldByOther ? 'opacity-80' : ''}`}
          data-testid={`live-lead-card-${idx}`}
        >
          <button
            onClick={() => dismiss(lead.lead_id)}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 flex items-center justify-center text-neutral-600 dark:text-neutral-300 transition-all"
            data-testid={`dismiss-lead-${idx}`}
            aria-label="Dismiss"
          >
            <X size={14} weight="bold" />
          </button>

          <div className="flex flex-wrap items-start gap-3 pr-8">
            <div className={`w-12 h-12 rounded-lg border-2 border-black flex items-center justify-center flex-shrink-0 ${iconBg}`}>
              {isReserved
                ? <Lock size={22} weight="fill" className="text-white" />
                : <Lightning size={22} weight="fill" className="text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {isMine ? (
                  <span className="px-2 py-0.5 text-[10px] font-black rounded-full border-2 border-black bg-emerald-500 text-white tabular-nums animate-pulse" data-testid={`reservation-badge-${idx}`}>
                    RESERVED · {remainingSec}s
                  </span>
                ) : isHeldByOther ? (
                  <span className="px-2 py-0.5 text-[10px] font-black rounded-full border-2 border-black bg-neutral-500 text-white tabular-nums" data-testid={`held-by-other-badge-${idx}`}>
                    HELD BY ANOTHER · {remainingSec}s
                  </span>
                ) : (
                  <span className="text-xs font-black uppercase tracking-wide text-[#475569]">Just Posted</span>
                )}
                <span className={`px-2 py-0.5 text-[10px] font-black rounded-full border border-black ${gradeColor(lead.lead_grade)}`} data-testid={`lead-grade-${idx}`}>
                  Grade {lead.lead_grade}
                </span>
                {!isReserved && (
                  <span className="text-[10px] text-neutral-500 dark:text-neutral-400 font-bold flex items-center gap-1">
                    <Clock size={11} weight="bold" /> just now
                  </span>
                )}
              </div>
              <h4 className="font-black text-neutral-900 dark:text-white text-sm leading-tight mb-1 truncate" data-testid={`lead-title-${idx}`}>
                {lead.job_type_display}{lead.title ? ` — ${lead.title}` : ''}
              </h4>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-600 dark:text-neutral-300 mb-2">
                <span className="flex items-center gap-1">
                  <MapPin size={12} weight="bold" className="text-[#2B4C6F]" />
                  {[lead.city, lead.state].filter(Boolean).join(', ') || 'Location not set'}
                </span>
                {(lead.budget_min || lead.budget_max) && (
                  <span className="flex items-center gap-1">
                    <CurrencyDollar size={12} weight="bold" className="text-emerald-500" />
                    Budget ${lead.budget_min || '?'}–${lead.budget_max || '?'}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Star size={12} weight="fill" className="text-amber-500" />
                  Quality {lead.lead_score}/100
                </span>
              </div>
              {lead.description && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400 italic line-clamp-2 mb-2">"{lead.description}"</p>
              )}
              {resInfo?.error && (
                <p className="text-xs text-rose-600 font-bold" data-testid={`reservation-error-${idx}`}>
                  {resInfo.error}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-700">
            <div className="text-xs text-neutral-500 dark:text-neutral-400">
              Lead price: <span className="font-black text-base text-neutral-900 dark:text-white">${lead.price}</span>
              {isMine && <span className="ml-2 text-[10px] font-bold text-emerald-600">· Held for you</span>}
              {isHeldByOther && <span className="ml-2 text-[10px] font-bold text-neutral-500">· Held by another</span>}
            </div>
            <div className="flex gap-2">
              {!isReserved && (
                <button
                  onClick={() => reserveLead(lead.lead_id)}
                  disabled={isReserving}
                  className="flex items-center gap-1.5 h-9 px-3 text-xs font-bold rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-60"
                  data-testid={`reserve-lead-${idx}`}
                  title="Hold this lead for 60 seconds while you decide"
                >
                  {isReserving
                    ? <SpinnerGap size={13} weight="bold" className="animate-spin" />
                    : <Lock size={13} weight="bold" />}
                  Hold 60s
                </button>
              )}
              <button
                onClick={() => dismiss(lead.lead_id)}
                className="h-9 px-3 text-xs font-bold rounded-lg border-2 border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all"
                data-testid={`later-lead-${idx}`}
              >
                Later
              </button>
              <button
                onClick={() => { dismiss(lead.lead_id); navigate(`/leads-marketplace?lead=${lead.lead_id}`); }}
                disabled={isHeldByOther}
                className="flex items-center gap-1.5 h-9 px-4 text-xs font-black rounded-lg bg-[#3B82F6] hover:bg-[#1E40AF] text-[#0F172A] border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`buy-lead-${idx}`}
                title={isHeldByOther ? 'Another contractor is holding this lead' : 'Buy this lead'}
              >
                <ShoppingCart size={13} weight="bold" />
                Buy Lead
              </button>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default LiveLeadPreview;
